import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { usePageMeta, shortDate } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import { toast } from "sonner";
import { cleanNarrative, PILLAR_COLOR, pillarLabel } from "./wevaUtils";
import { getPracticeConfigs } from "../practices/practiceConfigs";
import {
  ChevronLeft, ChevronDown, ChevronUp, Sparkles, Share2, Eye, Tag, Leaf,
  Lightbulb, ArrowRight, Compass,
} from "lucide-react";

const PILLAR_ICON = { realize: Eye, designate: Tag, release: Leaf };

function Accordion({ icon: Icon, iconColor, title, subtitle, count, accent, open, onToggle, children, testId }) {
  return (
    <div className={`overflow-hidden rounded-2xl border ${accent ? "border-primary/40 bg-primary/5" : "border-border bg-card"}`}>
      <button onClick={onToggle} data-testid={testId} className="flex w-full items-center gap-3 p-4 text-left">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border" style={{ backgroundColor: iconColor + "18", borderColor: iconColor }}>
          <Icon className="h-4.5 w-4.5" style={{ color: iconColor }} />
        </span>
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-medium ${accent ? "text-primary" : "text-foreground"}`}>{title}</p>
          {subtitle ? <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{subtitle}</p> : null}
        </div>
        {typeof count === "number" ? <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-medium text-primary">{count}</span> : null}
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>
      {open ? <div className="space-y-2 border-t border-border p-4 pt-3">{children}</div> : null}
    </div>
  );
}

function CompMetric({ label, value, accent, color }) {
  return (
    <div className={`flex-1 rounded-xl border p-3 ${accent ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`}>
      <p className={`text-xs ${accent ? "text-primary-foreground/85" : "text-muted-foreground"}`}>{label}</p>
      <p className="mt-1 font-display text-lg" style={!accent && color ? { color } : undefined}>{value}</p>
    </div>
  );
}

export default function WevaReport() {
  const { id } = useParams();
  const t = useT();
  const navigate = useNavigate();
  usePageMeta(t("weva_report"));
  const [report, setReport] = useState(undefined);
  const [comparison, setComparison] = useState(null);
  const [expanded, setExpanded] = useState({ narrative: true, recommended_practices: true });
  const configs = useMemo(() => getPracticeConfigs(t), [t]);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/weva/reports/${id}`); setReport(data); }
      catch { setReport(null); }
      try { const { data } = await api.get(`/weva/reports/${id}/comparison`); setComparison(data); }
      catch { /* noop */ }
    })();
  }, [id]);

  const toggle = (k) => setExpanded((p) => ({ ...p, [k]: !p[k] }));

  const recommended = useMemo(() => {
    const list = Array.isArray(report?.recommended_practices) ? report.recommended_practices : [];
    const primary = report?.next_practice;
    if (primary?.key && !list.some((r) => r.key === primary.key)) return [{ ...primary, primary: true }, ...list];
    return list.map((it) => ({ ...it, primary: primary?.key === it.key }));
  }, [report]);

  const share = async () => {
    if (!report) return;
    const text = `${cleanNarrative(report.narrative)}\n\n${(report.sections || []).map((s) => `— ${s.heading}\n${(s.bullets || []).map((b) => "• " + b).join("\n")}`).join("\n\n")}`;
    try { await navigator.clipboard.writeText(text); toast.success(t("common.copied")); } catch { /* noop */ }
  };

  if (report === undefined) return <Spinner label={t("common.loading")} />;

  return (
    <div className="space-y-4" data-testid="weva-report-view">
      <div className="flex items-center justify-between gap-3">
        <button onClick={() => navigate("/app/weva/reports")} data-testid="report-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ChevronLeft className="h-4 w-4" /> {t("weva_reports_title")}
        </button>
        {report ? (
          <button onClick={share} data-testid="report-share" className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
            <Share2 className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {!report ? (
        <p className="text-sm text-muted-foreground">{t("report_not_found")}</p>
      ) : (
        <>
          <div>
            <h1 className="font-display text-2xl text-foreground">{t("weva_report")}</h1>
            {report.created_at ? <p className="text-xs text-muted-foreground">{shortDate(report.created_at)}</p> : null}
          </div>

          {comparison?.has_previous ? (
            <button onClick={() => navigate(`/app/weva/report/${comparison.previous_id}`)} data-testid="report-comparison-card"
              className="w-full rounded-2xl border border-primary/40 border-l-4 border-l-primary bg-card p-4 text-left">
              <div className="mb-2 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-[11px] font-semibold uppercase tracking-wide text-primary">{t("since_your_last_report")}</span>
                <span className="ml-auto text-xs text-muted-foreground">{comparison.days_between}d</span>
              </div>
              <div className="flex gap-2">
                <CompMetric label={t("weva_comp_new_entries")} value={String(comparison.new_entries?.total ?? 0)} accent />
                <CompMetric label={t("weva_comp_practice_days")} value={String(comparison.days_practiced ?? 0)} />
              </div>
              {comparison.avg_mood_delta != null && (
                <div className="mt-2 flex gap-2">
                  <CompMetric label={t("weva_comp_mood_delta")} value={`${comparison.avg_mood_delta > 0 ? "+" : ""}${comparison.avg_mood_delta}`} color={comparison.avg_mood_delta < 0 ? "#059669" : comparison.avg_mood_delta > 0 ? "#DC2626" : undefined} />
                  <CompMetric label={t("weva_comp_beliefs")} value={`${comparison.beliefs?.current ?? 0}${comparison.beliefs?.delta ? ` (${comparison.beliefs.delta > 0 ? "+" : ""}${comparison.beliefs.delta})` : ""}`} />
                </div>
              )}
              <p className="mt-2 text-xs font-medium text-primary">{t("tap_to_open_previous_report")}</p>
            </button>
          ) : comparison ? (
            <div className="rounded-2xl border border-primary/40 border-l-4 border-l-primary bg-card p-4">
              <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /><span className="text-[11px] font-semibold uppercase tracking-wide text-primary">{t("this_is_your_first_weva_report")}</span></div>
              <p className="mt-1.5 text-sm text-muted-foreground">{t("welcome_to_the_journey_come_back_after")}</p>
            </div>
          ) : null}

          <Accordion testId="acc-narrative" icon={Sparkles} iconColor="#5E7A73" title={t("weva_reflects_title")} subtitle={t("weva_reflects_sub")} accent open={!!expanded.narrative} onToggle={() => toggle("narrative")}>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">{cleanNarrative(report.narrative)}</p>
          </Accordion>

          {(report.sections || []).map((sec) => {
            const key = `pillar-${sec.pillar}`;
            const color = PILLAR_COLOR[sec.pillar] || "#5E7A73";
            const Icon = PILLAR_ICON[sec.pillar] || Leaf;
            return (
              <Accordion key={key} testId={`acc-${key}`} icon={Icon} iconColor={color} title={sec.heading} subtitle={pillarLabel(sec.pillar)} count={sec.bullets?.length} open={!!expanded[key]} onToggle={() => toggle(key)}>
                {(sec.bullets || []).map((b, j) => (
                  <div key={j} className="flex gap-2 text-sm text-foreground"><span style={{ color }}>•</span><span>{b}</span></div>
                ))}
              </Accordion>
            );
          })}

          {recommended.length > 0 && (
            <Accordion testId="acc-recommended-practices" icon={Compass} iconColor="#5E7A73" title={t("weva_recommended_title")} subtitle={t("weva_recommended_sub")} count={recommended.length} open={!!expanded.recommended_practices} onToggle={() => toggle("recommended_practices")}>
              {recommended.map((rp, i) => {
                const cfg = configs[rp.key];
                const color = cfg ? (PILLAR_COLOR[cfg.pillar] || "#5E7A73") : "#5E7A73";
                const Icon = cfg?.icon || Leaf;
                return (
                  <button key={rp.key + i} onClick={() => navigate(`/app/practices/${rp.key}`)} data-testid={`recommended-practice-${rp.key}`}
                    className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${rp.primary ? "border-primary bg-primary/10" : "border-border bg-card hover:bg-accent/30"}`}>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border" style={{ backgroundColor: color + "22", borderColor: color }}>
                      <Icon className="h-5 w-5" style={{ color }} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-foreground">{cfg?.title || rp.key}</span>
                        {rp.primary ? <span className="rounded-full bg-primary px-2 py-0.5 text-[9px] font-semibold uppercase text-primary-foreground">{t("try_this_first")}</span> : null}
                      </div>
                      {rp.reason ? <p className="mt-0.5 text-xs text-muted-foreground">{rp.reason}</p> : null}
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                );
              })}
            </Accordion>
          )}

          {(report.detected_beliefs || []).length > 0 && (
            <Accordion testId="acc-beliefs" icon={Lightbulb} iconColor="#3E6478" title={t("weva_beliefs_noticed_title")} count={report.detected_beliefs.length} open={!!expanded.detected_beliefs} onToggle={() => toggle("detected_beliefs")}>
              {report.detected_beliefs.map((b, i) => (
                <div key={i} className="border-b border-border py-2 last:border-0">
                  <p className="text-sm italic text-foreground">&ldquo;{b.belief}&rdquo;</p>
                  {b.frequency ? <p className="mt-0.5 text-xs text-muted-foreground">{b.frequency}{b.body_location ? ` · ${b.body_location}` : ""}</p> : null}
                </div>
              ))}
              <button onClick={() => navigate("/app/within/beliefs")} data-testid="report-open-beliefs" className="mt-1 text-xs font-medium text-primary hover:underline">{t("open_the_beliefs_table")}</button>
            </Accordion>
          )}
        </>
      )}
    </div>
  );
}
